/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps, IconButton } from '@fluentui/react/lib/Button';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { IntentPageUIConfigStore, PageSize } from 'src/luis/appShell/buildPane/intentPage/stores/IntentPageUIConfigStore';
import { Model } from 'src/luis/types/Model';
import { IntentLabelingSidePane } from 'src/luis/verticals/labelingSidePane/IntentLabelingSidePane';
import { FiltersBlade } from 'src/verticals/utterancesFilters/FiltersBlade';
import { SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

export type IntentPageSidePanelContainerProps = {
    utterancesCount: number;
    onPanelClose: () => void;
    model: Omit<Model, 'name'>;
    pageConfigsStore: IntentPageUIConfigStore;
    onFiltersChange: (change: SelectionChange) => void;
};

const messages = defineMessages({
    filter: {
        id: 'luis.intentPageSidePanelContainer.filter',
        defaultMessage: 'Apply filters'
    },
    clearAll: {
        id: 'luis.intentPageSidePanelContainer.clearAll',
        defaultMessage: 'Clear all filters'
    },
    closeButtonAriaLabel: {
        id: 'luis.intentPageSidePanelContainer.closeButtonAriaLabel',
        defaultMessage: 'Close filters'
    }
});

const scopedTheme = getScopedTheme('IntentPageSidePanelContainer');
const { styleComponent } = getStylistV2('IntentPageSidePanelContainer');
const ClearButtonIcon = styleComponent<IButtonProps>(ActionButton)('ClearButtonIcon', { fontSize: 16 });
const Container = styleComponent<IStackProps>(Stack)('Container', theme => ({ width: theme.width, borderLeft: theme.borderSpecs }));

/**
 * Represents a container for the intent page filters and labeling blades.
 */
export const IntentPageSidePanelContainer = observer((props: IntentPageSidePanelContainerProps) => {
    const { model, utterancesCount, pageConfigsStore, onFiltersChange, onPanelClose } = props;
    const { dispatcher } = useGlobalState();
    const { localize } = useLocalization();

    const { labelingStore, opsStore } = pageConfigsStore;
    const filtersStore = pageConfigsStore.intentFiltersStore;

    const toggleLabeling = React.useCallback((on: boolean) => labelingStore.setLabelingMode(on ? 'brush' : 'menu'), []);

    const handleChangePageSize = React.useCallback(
        (size: PageSize) => {
            pageConfigsStore.setCurrentPageSize(size);
            dispatcher.dispatch('changeUtterancePageSize', { model, newPageSize: pageConfigsStore.currentPageSize });
        },
        [dispatcher.dispatch, model, pageConfigsStore]
    );

    const filtersBladeHeader = (
        <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
            <LuisText fontWeight={600} color={scopedTheme.titlesColor}>
                {localize(messages.filter)}
            </LuisText>

            <LuisTooltip content={localize(messages.clearAll)}>
                <ClearButtonIcon
                    iconProps={{ iconName: 'ClearFilter' }}
                    ariaLabel={localize(messages.clearAll)}
                    disabled={!filtersStore.isFilterModeOn}
                    onClick={() => filtersStore.clearFilters()}
                    styles={{ icon: { color: scopedTheme.clearIconColor }, iconHovered: { color: scopedTheme.clearIconColor } }}
                />
            </LuisTooltip>
        </Stack>
    );

    return (
        pageConfigsStore.activeBladeType !== 'none' && (
            <Container role="complementary">
                <IconButton
                    autoFocus
                    onClick={onPanelClose}
                    iconProps={{ iconName: 'ChromeClose' }}
                    styles={{ root: { alignSelf: 'flex-end' } }}
                    ariaLabel={localize(messages.closeButtonAriaLabel)}
                />

                {pageConfigsStore.activeBladeType === 'entityPalette' && (
                    <IntentLabelingSidePane
                        labelingMode={labelingStore.mode}
                        onToggleLabeling={toggleLabeling}
                        entitySelectionStore={pageConfigsStore.sidePaneEntitySelectionStore}
                        onStartCreateEntity={() => opsStore.setModalsConfigState({ isEntityModalOpen: true })}
                        onChangeSelection={selection => pageConfigsStore.sidePaneEntitySelectionStore.setSelection(selection)}
                    />
                )}

                {pageConfigsStore.activeBladeType === 'viewOptions' && (
                    <FiltersBlade
                        filtersStore={filtersStore}
                        utterancesCount={utterancesCount}
                        headerElement={filtersBladeHeader}
                        onSelectionChange={onFiltersChange}
                        onChangePageSize={handleChangePageSize}
                        pageSize={pageConfigsStore.currentPageSize}
                        displayedFilters={['score', 'entities', 'nearestIntents', 'difference', 'pageCount']}
                    />
                )}
            </Container>
        )
    );
});
